import React, { useEffect, useState } from 'react'
import { Button } from 'antd'
import { useHistory, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'

import { isPlanBoostEndingSoon } from '@vacationtracker/shared/functions/plan-boost'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import IntlMessages from '../../util/IntlMessages'
import { FrontendUrls } from '../../types/urls'
import PlanDowngradeModal from '../plan-downgrade-modal'
import { useCurrentTime } from './use-current-time'
import { PlanBoostEndingDescription } from './plan-boost-ending-description'
import { PlanBoostEndingWarningModal } from './plan-boost-ending-warning-modal'
import { usePlanBoostDowngradeData } from './use-plan-boost-downgrade-data'
import { onOpenPlanBoostEndingModal } from './open-event'

export const getPlanBoostDismissalKey = (companyId: string): string => `planBoostEndingModalDismissedOn:${companyId}`

const PlanBoostEndingModal = (): React.ReactElement | null => {
  const history = useHistory()
  const { pathname } = useLocation()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { featureFlags, isLoadingFeatureFlags } = useAppSelector(selectFeatureFlagsSlice)

  const isAdmin = authUser.role === 'Admin'
  const planBoost = authCompany?.planBoost
  const now = useCurrentTime(isAdmin && authCompany?.plan === SubscriptionPlanEnum.core && planBoost?.status === 'active')
  const isEndingSoon = isAdmin && isPlanBoostEndingSoon(authCompany, now)

  const dismissalKey = getPlanBoostDismissalKey(authCompany?.id ?? '')
  const today = dayjs(now).format('YYYY-MM-DD')
  const [dismissedOn, setDismissedOn] = useState(() => localStorage.getItem(dismissalKey))
  const [isResolved, setIsResolved] = useState(false)
  const [isRequested, setIsRequested] = useState(false)

  useEffect(
    () =>
      onOpenPlanBoostEndingModal(() => {
        setIsRequested(true)
        setIsResolved(false)
      }),
    []
  )

  // The billing page is where "Upgrade to Complete" leads, so the modal must not pop up over it on
  // its own. Asked for from the banner's "Review changes", it opens there too.
  const isOnBillingPage = pathname.startsWith(FrontendUrls.billing)
  const isVisible = isEndingSoon && !isResolved && (isRequested || (dismissedOn !== today && !isOnBillingPage))

  // Until feature flags load, unlimited-location or unlimited-department companies would look over the limits
  const { data, reload } = usePlanBoostDowngradeData(isVisible && !isLoadingFeatureFlags, featureFlags)

  if (!isVisible || !planBoost || !data) {
    return null
  }

  const handleCancel = () => {
    localStorage.setItem(dismissalKey, today)
    setDismissedOn(today)
    setIsRequested(false)
  }

  const handleUpgrade = () => {
    handleCancel()
    history.push(FrontendUrls.billing)
  }

  // Nothing to pick within the Core limits, so admins only get a warning about the features they lose
  if (!data.isOverCoreLimits) {
    return <PlanBoostEndingWarningModal expiresAt={planBoost.expiresAt} onClose={handleCancel} onUpgrade={handleUpgrade} />
  }

  // PlanDowngradeModal has already sent the deletion events by now; no BILLING_UPDATED, the plan is already Core
  const handleSubmit = () => {
    setIsResolved(true)
    setIsRequested(false)
    return Promise.resolve()
  }

  return (
    <PlanDowngradeModal
      visible
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onSubmitFailed={reload}
      sourceData={data.sourceData}
      downgradeType="complete-to-core"
      targetPlan="Core"
      source="complete_boost"
      closable
      requireFullSelection
      boostCopy={{
        title: <IntlMessages id="completeBoost.modal.title" />,
        description: <PlanBoostEndingDescription expiresAt={planBoost.expiresAt} />,
        submitLabel: <IntlMessages id="completeBoost.modal.submit" />,
      }}
      extraFooterAction={
        <Button onClick={handleUpgrade}>
          <IntlMessages id="completeBoost.upgradeToComplete" />
        </Button>
      }
    />
  )
}

export default PlanBoostEndingModal
